import { useState } from 'react';

import { PlaceholderMemberService } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { GroupRole, InvitationResponseDto } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { ShareIcon } from '@/components/common/ShareIcon';
import { MemberActionRow } from '@/components/members/MemberActionRow';

import {
  useCreateInvitationMutation,
  useInvitationLink,
} from '@/hooks/queries/useInvitations';

import { cn } from '@/lib/utils';

import { InviteDialog } from './InviteDialog';

const COPIED_FEEDBACK_MS = 2000;

/**
 * Copies text that is still being produced, e.g. a link the API is minting.
 * Must be called synchronously from the click handler: Safari revokes the
 * click's transient user activation across a network await, so a `writeText`
 * after it rejects. A `ClipboardItem` built from a pending Blob is authorised
 * at `write` time and filled in when the promise settles; if `text` rejects
 * or yields nothing, the write aborts and the clipboard is left untouched.
 * Resolves to whether the text reached the clipboard.
 */
export async function copyPendingText(
  text: Promise<string | null>
): Promise<boolean> {
  const clipboard =
    typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (!clipboard) return false;

  if (
    typeof ClipboardItem !== 'undefined' &&
    typeof clipboard.write === 'function'
  ) {
    const blob = text.then(value => {
      if (!value) throw new Error('Nothing to copy');
      return new Blob([value], { type: 'text/plain' });
    });
    // The browser may never read the blob (e.g. the write is refused up
    // front), which would otherwise surface as an unhandled rejection.
    blob.catch(() => undefined);
    try {
      await clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
      return true;
    } catch {
      // Older Chromium exposes ClipboardItem but refuses a Promise-valued
      // one; its writeText needs focus, not activation, so it still lands.
      // Where activation is gone (Safari) this attempt fails harmlessly.
    }
  }

  try {
    const value = await text;
    if (!value) return false;
    await clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

interface InvitationLinkProps {
  groupId: string;
  groupName: string;
  /** The viewer's role; owners and admins may add members without an account. */
  userRole?: GroupRole;
  /** Offered from the dialog to owners and admins only. */
  onAddPlaceholder?: () => void;
  className?: string;
  /** `row` renders the member-row entry used by the Members tab. */
  presentation?: 'button' | 'row';
}

export function InvitationLink({
  groupId,
  groupName,
  userRole,
  onAddPlaceholder,
  className,
  presentation = 'button',
}: InvitationLinkProps) {
  const { t } = useTranslation();
  useNamespace('common');
  useNamespace('groups');

  const [invitation, setInvitation] = useState<InvitationResponseDto | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(false);

  const createInvitation = useCreateInvitationMutation(groupId);
  const {
    copyToClipboard,
    shareLink,
    getFullLink,
    getShareMessage,
    isExpired,
  } = useInvitationLink(invitation, groupName);

  const flashCopied = () => {
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), COPIED_FEEDBACK_MS);
  };

  const handleOpen = async () => {
    if (invitation && !isExpired()) {
      setDialogOpen(true);
      return;
    }

    const pending = createInvitation.mutateAsync();
    // Auto-copy the moment the invite exists: the common case is pasting it
    // straight into a chat. Started before the await to keep the click's
    // user activation (see copyPendingText).
    const copied = copyPendingText(
      pending.then(created =>
        created.link ? getShareMessage(created.link) : null
      )
    );

    let created: InvitationResponseDto;
    try {
      created = await pending;
    } catch {
      // Reported by the mutation's own error toast.
      return;
    }
    setInvitation(created);
    setDialogOpen(true);

    // On failure the Copy button in the dialog is the fallback.
    if (await copied) flashCopied();
  };

  const handleCopy = async () => {
    if (await copyToClipboard()) flashCopied();
  };

  const canAddPlaceholder =
    !!onAddPlaceholder && PlaceholderMemberService.canAdd(userRole);

  const handleAddPlaceholder = () => {
    setDialogOpen(false);
    onAddPlaceholder?.();
  };

  const dialog = invitation ? (
    <InviteDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      groupName={groupName}
      link={getFullLink()}
      copied={justCopied}
      onCopy={handleCopy}
      onShare={shareLink}
      onAddPlaceholder={canAddPlaceholder ? handleAddPlaceholder : undefined}
    />
  ) : null;

  if (presentation === 'row') {
    return (
      <div className={className}>
        <MemberActionRow
          icon={ShareIcon}
          label={t('groups:placeholderMembers.inviteCta')}
          description={t('groups:placeholderMembers.inviteDescription')}
          onClick={handleOpen}
          disabled={createInvitation.isPending}
          loading={createInvitation.isPending}
          testId="invite-members-row"
        />
        {dialog}
      </div>
    );
  }

  return (
    <>
      <Button
        variant="primary"
        className={cn('w-full', className)}
        onClick={handleOpen}
        disabled={createInvitation.isPending}
        data-testid="invite-button"
      >
        {createInvitation.isPending ? (
          t('common:loading.text')
        ) : (
          <>
            <ShareIcon className="w-4 h-4 mr-1.5" />
            <span>{t('common:invitations.quickInviteLink')}</span>
          </>
        )}
      </Button>
      {dialog}
    </>
  );
}
